import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { apiFetch } from "../api";
import { useAuth } from "../AuthContext";
import { usePublicI18n } from "../PublicI18nContext";
import useVisibilityAwareInterval from "../Hooks/useVisibilityAwareInterval";
import { toEasternLocaleString } from "../utils";
import PublicAssistantChat from "./PublicAssistantChat";

const emptyState = {
  applications: [],
  supportRequests: [],
};

function formatApplicationOption(item) {
  const label = item.kind === "permit"
    ? `${item.permit_type || "Permit"} application`
    : `${item.license_type?.name || "License"} application`;
  return `${label} #${item.id}`;
}

function MessageBubble({ message, ownSender }) {
  const { t } = usePublicI18n();
  const isOwn = message.sender_type === ownSender;
  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[82%] ${isOwn ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <span className="px-1 text-[11px] font-medium text-gray-500">
          {isOwn ? t("helpWidget.you") : message.sender_name || t("helpWidget.staff")}
        </span>
        <div
          className={`rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm ${
            isOwn
              ? "rounded-tr-sm bg-indigo-600 text-white"
              : "rounded-tl-sm bg-gray-100 text-gray-900"
          }`}
        >
          <span className="whitespace-pre-wrap break-words">{message.body}</span>
        </div>
        <span className="px-1 text-[11px] text-gray-500">{toEasternLocaleString(message.created_at)}</span>
      </div>
    </div>
  );
}

function SupportThread({ request, onSend, sending }) {
  const { t } = usePublicI18n();
  const replyId = React.useId();
  const [body, setBody] = useState("");
  const threadRef = useRef(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [request?.messages?.length]);

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    const sent = await onSend(trimmed);
    if (sent) setBody("");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={threadRef} aria-live="polite" className="min-h-[220px] flex-1 space-y-3 overflow-y-auto rounded-xl border border-gray-100 bg-white p-3">
        {(request?.messages || []).length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">{t("helpWidget.noMessages")}</p>
        ) : (
          request.messages.map((message) => (
            <MessageBubble key={message.id} message={message} ownSender="public" />
          ))
        )}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <label htmlFor={replyId} className="sr-only">{t("helpWidget.replyPlaceholder")}</label>
        <textarea
          id={replyId}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={2}
          placeholder={t("helpWidget.replyPlaceholder")}
          className="min-h-[48px] flex-1 resize-none rounded-xl border-gray-200 text-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!body.trim() || sending}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="sr-only">{t("helpWidget.sendReply")}</span>
          <PaperAirplaneIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default function PublicHelpWidget() {
  const { user } = useAuth();
  const { t } = usePublicI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("assistant");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [step, setStep] = useState("choice");
  const [data, setData] = useState(emptyState);
  const [selectedApplication, setSelectedApplication] = useState("");
  const [generalBody, setGeneralBody] = useState("");
  const [applicationBody, setApplicationBody] = useState("");
  const [activeRequest, setActiveRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const closeButtonRef = useRef(null);
  const launcherRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Dialog focus management: move focus in on open, restore to the launcher
  // on close (WCAG 2.4.3). Skips initial mount so page focus is undisturbed.
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      closeButtonRef.current?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      launcherRef.current?.focus();
    }
  }, [open]);

  const handlePanelKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
    }
  };

  const isPublicUser = user?.user_type === "public";
  const isStaffUser = Boolean(user) && user.user_type !== "public";
  const sourcePath = `${location.pathname}${location.search}`;

  const applicationOptions = useMemo(() => data.applications, [data.applications]);
  const hasApplications = applicationOptions.length > 0;

  const openSupportRequest = useCallback(async (requestId) => {
    const response = await apiFetch(`/my-support-requests/${requestId}`);
    if (!response.ok) throw new Error("Unable to load support request.");
    const request = await response.json();
    setActiveRequest(request);
    return request;
  }, []);

  const loadWidgetData = useCallback(async () => {
    if (!isPublicUser) return;
    setLoading(true);
    setError("");
    try {
      const [licenseRes, permitRes, supportRes] = await Promise.all([
        apiFetch("/my-applications"),
        apiFetch("/my-permit-applications"),
        apiFetch("/my-support-requests"),
      ]);
      if (!licenseRes.ok || !permitRes.ok || !supportRes.ok) {
        throw new Error("Unable to load help options.");
      }
      const [licenses, permits, supportRequests] = await Promise.all([
        licenseRes.json(),
        permitRes.json(),
        supportRes.json(),
      ]);
      const combinedApplications = [
        ...(Array.isArray(licenses) ? licenses.map((item) => ({ ...item, kind: "license" })) : []),
        ...(Array.isArray(permits) ? permits.map((item) => ({ ...item, kind: "permit" })) : []),
      ];
      setData({
        applications: combinedApplications,
        supportRequests: Array.isArray(supportRequests) ? supportRequests : [],
      });
      setSelectedApplication(combinedApplications[0] ? `${combinedApplications[0].kind}:${combinedApplications[0].id}` : "");
      const latestOpen = (Array.isArray(supportRequests) ? supportRequests : []).find((request) => request.status === "open");
      if (!combinedApplications.length) {
        setStep(latestOpen ? "general-thread" : "general");
      } else {
        setStep("choice");
      }
      if (latestOpen) {
        await openSupportRequest(latestOpen.id);
      } else {
        setActiveRequest(null);
      }
    } catch (err) {
      setError(err.message || t("helpWidget.errorLoad"));
    } finally {
      setLoading(false);
    }
  }, [isPublicUser, openSupportRequest, t]);

  useEffect(() => {
    if (open) {
      loadWidgetData();
    }
  }, [open, loadWidgetData]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiFetch("/settings/public-chat");
        if (!response.ok) throw new Error("unavailable");
        const setting = await response.json();
        if (!cancelled) {
          const enabled = Boolean(setting?.enabled);
          setAiEnabled(enabled);
          if (!enabled) setTab("staff");
        }
      } catch (err) {
        if (!cancelled) {
          setAiEnabled(false);
          setTab("staff");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const refreshActiveRequest = useCallback(async () => {
    if (!open || !activeRequest?.id) return;
    try {
      await openSupportRequest(activeRequest.id);
    } catch {
      // Keep the current thread visible if a background refresh fails.
    }
  }, [activeRequest?.id, open, openSupportRequest]);

  useVisibilityAwareInterval(refreshActiveRequest, open && activeRequest?.id ? 10000 : null);

  useEffect(() => {
    if (!open) return undefined;
    const handleNotification = () => {
      refreshActiveRequest();
    };
    window.addEventListener("notifications:created", handleNotification);
    window.addEventListener("notifications:refresh", handleNotification);
    return () => {
      window.removeEventListener("notifications:created", handleNotification);
      window.removeEventListener("notifications:refresh", handleNotification);
    };
  }, [open, refreshActiveRequest]);

  // Staff have their own internal assistant (ChatWidget); everyone else —
  // anonymous visitors included — gets the public help widget.
  if (isStaffUser) return null;

  const resetMessages = () => {
    setError("");
    setNotice("");
  };

  const handleApplicationSend = async () => {
    const body = applicationBody.trim();
    if (!selectedApplication || !body || sending) return;
    resetMessages();
    setSending(true);
    try {
      const [kind, id] = selectedApplication.split(":");
      const endpoint = kind === "permit"
        ? `/my-permit-applications/${id}/messages`
        : `/my-applications/${id}/messages`;
      const response = await apiFetch(endpoint, {
        method: "POST",
        body: { body },
      });
      if (!response.ok) throw new Error(t("helpWidget.errorSendMessage"));
      setApplicationBody("");
      setNotice(t("helpWidget.messageSent"));
    } catch (err) {
      setError(err.message || t("helpWidget.errorSendMessage"));
    } finally {
      setSending(false);
    }
  };

  const handleCreateGeneralRequest = async () => {
    const body = generalBody.trim();
    if (!body || sending) return;
    resetMessages();
    setSending(true);
    try {
      const response = await apiFetch("/my-support-requests", {
        method: "POST",
        body: {
          subject: "Public portal help request",
          body,
          source_path: sourcePath,
        },
      });
      if (!response.ok) throw new Error(t("helpWidget.errorSendRequest"));
      const request = await response.json();
      setActiveRequest(request);
      setGeneralBody("");
      setStep("general-thread");
      setNotice(t("helpWidget.requestSent"));
    } catch (err) {
      setError(err.message || t("helpWidget.errorSendRequest"));
    } finally {
      setSending(false);
    }
  };

  const handleSupportReply = async (body) => {
    if (!activeRequest || sending) return false;
    resetMessages();
    setSending(true);
    try {
      const response = await apiFetch(`/my-support-requests/${activeRequest.id}/messages`, {
        method: "POST",
        body: { body },
      });
      if (!response.ok) throw new Error(t("helpWidget.errorSendReply"));
      const message = await response.json();
      setActiveRequest((prev) => ({
        ...prev,
        messages: [...(prev?.messages || []), message],
      }));
      return true;
    } catch (err) {
      setError(err.message || t("helpWidget.errorSendReply"));
      return false;
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed z-[70] font-sans"
      style={{
        bottom: 'calc(1.25rem + env(safe-area-inset-bottom))',
        right: 'calc(1.25rem + env(safe-area-inset-right))',
      }}
      data-testid="public-help-widget"
    >
      {open && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape-to-close on a non-modal dialog
        <div
          id="public-help-panel"
          role="dialog"
          aria-label={t("helpWidget.title")}
          onKeyDown={handlePanelKeyDown}
          className="mb-3 flex h-[min(640px,calc(100vh-7rem))] w-[min(390px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          style={{ maxHeight: 'calc(100dvh - 7rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
            <div>
              <h2 className="text-sm font-black text-gray-900">{t("helpWidget.title")}</h2>
              <p className="text-xs text-gray-500">{t("helpWidget.subtitle")}</p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <span className="sr-only">{t("shared.close")}</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {aiEnabled && (
            <div className="flex border-b border-gray-100" role="tablist" aria-label={t("helpWidget.title")}>
              <button
                type="button"
                role="tab"
                id="public-help-tab-assistant"
                aria-selected={tab === "assistant"}
                aria-controls="public-help-tabpanel"
                tabIndex={tab === "assistant" ? 0 : -1}
                onClick={() => setTab("assistant")}
                onKeyDown={(event) => {
                  if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                    setTab("staff");
                    document.getElementById("public-help-tab-staff")?.focus();
                  }
                }}
                className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition ${
                  tab === "assistant"
                    ? "border-b-2 border-brand text-brand"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                {t("helpWidget.tabAssistant")}
              </button>
              <button
                type="button"
                role="tab"
                id="public-help-tab-staff"
                aria-selected={tab === "staff"}
                aria-controls="public-help-tabpanel"
                tabIndex={tab === "staff" ? 0 : -1}
                onClick={() => setTab("staff")}
                onKeyDown={(event) => {
                  if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                    setTab("assistant");
                    document.getElementById("public-help-tab-assistant")?.focus();
                  }
                }}
                className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition ${
                  tab === "staff"
                    ? "border-b-2 border-brand text-brand"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden="true" />
                {t("helpWidget.tabStaff")}
              </button>
            </div>
          )}

          {tab === "assistant" && aiEnabled ? (
            <div
              id="public-help-tabpanel"
              role="tabpanel"
              aria-labelledby="public-help-tab-assistant"
              className="flex min-h-0 flex-1 flex-col p-4"
            >
              <PublicAssistantChat onSwitchToStaff={() => setTab("staff")} />
            </div>
          ) : !isPublicUser ? (
            <div
              id={aiEnabled ? "public-help-tabpanel" : undefined}
              role={aiEnabled ? "tabpanel" : undefined}
              aria-labelledby={aiEnabled ? "public-help-tab-staff" : undefined}
              className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
            >
              <p className="text-sm text-gray-700">{t("helpWidget.anonStaffIntro")}</p>
              <div className="flex gap-2">
                <Link
                  to="/public-login"
                  className="flex-1 rounded-xl bg-brand px-4 py-3 text-center text-sm font-black text-brand-fg hover:bg-brand-hover"
                >
                  {t("helpWidget.anonSignIn")}
                </Link>
                <Link
                  to="/register"
                  className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  {t("helpWidget.anonRegister")}
                </Link>
              </div>
              <p className="mt-2 text-sm text-gray-600">{t("helpWidget.anonConcernHint")}</p>
              <Link
                to="/resident-concern"
                className="rounded-xl border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {t("helpWidget.anonConcernLink")}
              </Link>
            </div>
          ) : (
          <div
            id={aiEnabled ? "public-help-tabpanel" : undefined}
            role={aiEnabled ? "tabpanel" : undefined}
            aria-labelledby={aiEnabled ? "public-help-tab-staff" : undefined}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4"
          >
            {loading ? (
              <p role="status" className="py-10 text-center text-sm text-gray-500">{t("helpWidget.loadingOptions")}</p>
            ) : (
              <>
                {step !== "choice" && hasApplications && (
                  <button
                    type="button"
                    onClick={() => {
                      resetMessages();
                      setStep("choice");
                    }}
                    className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                  >
                    <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                    {t("shared.back")}
                  </button>
                )}

                {error && <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
                {notice && <p role="status" className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{notice}</p>}

                {step === "choice" && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-900">{t("helpWidget.choicePrompt")}</p>
                    <button
                      type="button"
                      onClick={() => {
                        resetMessages();
                        setStep("application");
                      }}
                      className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-left text-sm font-semibold text-indigo-900 hover:bg-indigo-100"
                    >
                      {t("helpWidget.chooseApplication")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        resetMessages();
                        setStep(activeRequest ? "general-thread" : "general");
                      }}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50"
                    >
                      {t("helpWidget.generalHelp")}
                    </button>
                  </div>
                )}

                {step === "application" && (
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-900" htmlFor="public-help-application">
                      {t("helpWidget.selectApplication")}
                    </label>
                    <select
                      id="public-help-application"
                      value={selectedApplication}
                      onChange={(event) => setSelectedApplication(event.target.value)}
                      className="w-full rounded-xl border-gray-200 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      {applicationOptions.map((item) => (
                        <option key={`${item.kind}:${item.id}`} value={`${item.kind}:${item.id}`}>
                          {formatApplicationOption(item)}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="public-help-application-body" className="sr-only">{t("helpWidget.messageLabel")}</label>
                    <textarea
                      id="public-help-application-body"
                      value={applicationBody}
                      onChange={(event) => setApplicationBody(event.target.value)}
                      rows={5}
                      placeholder={t("helpWidget.describeHelpPlaceholder")}
                      className="w-full resize-none rounded-xl border-gray-200 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleApplicationSend}
                      disabled={!applicationBody.trim() || !selectedApplication || sending}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-black text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                      {sending ? t("helpWidget.sending") : t("helpWidget.sendToApplicationStaff")}
                    </button>
                  </div>
                )}

                {step === "general" && (
                  <div className="space-y-3">
                    <label htmlFor="public-help-general-body" className="block text-sm font-semibold text-gray-900">{t("helpWidget.generalPrompt")}</label>
                    <textarea
                      id="public-help-general-body"
                      value={generalBody}
                      onChange={(event) => setGeneralBody(event.target.value)}
                      rows={6}
                      placeholder={t("helpWidget.generalPlaceholder")}
                      className="w-full resize-none rounded-xl border-gray-200 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleCreateGeneralRequest}
                      disabled={!generalBody.trim() || sending}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-black text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                      {sending ? t("helpWidget.sending") : t("helpWidget.sendHelpRequest")}
                    </button>
                    {data.supportRequests.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setStep("general-thread")}
                        className="w-full text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                      >
                        {t("helpWidget.viewRecentThread")}
                      </button>
                    )}
                  </div>
                )}

                {step === "general-thread" && (
                  activeRequest ? (
                    <SupportThread request={activeRequest} onSend={handleSupportReply} sending={sending} />
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">{t("helpWidget.startThreadIntro")}</p>
                      <button
                        type="button"
                        onClick={() => setStep("general")}
                        className="w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-black text-white hover:bg-indigo-500"
                      >
                        {t("helpWidget.startRequest")}
                      </button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
          )}
        </div>
      )}
      <button
        ref={launcherRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? "public-help-panel" : undefined}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand text-brand-fg shadow-xl ring-4 ring-white transition hover:bg-brand-hover focus:outline-none focus:ring-indigo-200"
      >
        <span className="sr-only">{open ? t("shared.close") : t("helpWidget.openLabel")}</span>
        {open ? <XMarkIcon className="h-7 w-7" aria-hidden="true" /> : <ChatBubbleLeftRightIcon className="h-7 w-7" aria-hidden="true" />}
      </button>
    </div>
  );
}
